import {
  IconAlertTriangle,
  IconInfoCircle,
  IconSearch,
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { ApplogEntryDto } from 'src/api/FiretrolApiClient';
import { useApplogs } from 'src/api/queries/applogs/useApplogs';
import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { Select } from 'src/design/atoms/Select';
import { Stack } from 'src/design/atoms/Stack';
import { TextInput } from 'src/design/atoms/TextInput';
import { Header } from 'src/design/organisms/Header';
import useDebounce from 'src/hooks/useDebounce';
import {
  APPLOG_LEVEL_OPTIONS,
  APPLOG_LIMIT,
  APPLOG_SEARCH_MAX_LENGTH,
  APPLOG_TIME_RANGE_OPTIONS,
  ApplogTimeRange,
  DEFAULT_APPLOG_LEVEL,
  DEFAULT_APPLOG_TIME_RANGE,
  getApplogsErrorMessage,
  getTimeRangeMinutes,
  parseApplogLevel,
  parseApplogTimeRange,
} from 'src/routes/Settings/Applogs/applogFilters';
import { ApplogDetailModal } from 'src/routes/Settings/Applogs/components/ApplogDetailModal';
import { ApplogsTable } from 'src/routes/Settings/Applogs/components/ApplogsTable';

const EMPTY_ENTRIES: ApplogEntryDto[] = [];

export const Applogs: React.FC = () => {
  const [timeRange, setTimeRange] = useState<ApplogTimeRange>(
    DEFAULT_APPLOG_TIME_RANGE,
  );
  const [minLevel, setMinLevel] = useState(DEFAULT_APPLOG_LEVEL);
  const [search, setSearch] = useState('');
  const [selectedEntry, setSelectedEntry] = useState<ApplogEntryDto | null>(
    null,
  );
  const debouncedSearch = useDebounce(search, 300);

  const applogsQuery = useApplogs({
    windowMinutes: getTimeRangeMinutes(timeRange),
    minLevel,
    search: debouncedSearch || undefined,
    limit: APPLOG_LIMIT,
  });

  const entries = applogsQuery.data?.entries ?? EMPTY_ENTRIES;
  const isConfigured = applogsQuery.data?.isConfigured ?? true;
  const isPartial = applogsQuery.data?.isPartial ?? false;
  const hasMore = applogsQuery.data?.hasMore ?? false;

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'App Logs' }],
        }}
        title="App Logs"
        helpText="Search and monitor the application logs this environment writes to Azure Application Insights."
      />
      <Stack>
        <Group align="flex-end">
          <Select
            label="Time range"
            data={APPLOG_TIME_RANGE_OPTIONS}
            value={timeRange}
            onChange={(value) => setTimeRange(parseApplogTimeRange(value))}
            allowDeselect={false}
            w={200}
          />
          <Select
            label="Minimum level"
            data={APPLOG_LEVEL_OPTIONS}
            value={String(minLevel)}
            onChange={(value) => setMinLevel(parseApplogLevel(value))}
            allowDeselect={false}
            w={180}
          />
          <TextInput
            label="Search"
            placeholder="Search messages and exceptions..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            maxLength={APPLOG_SEARCH_MAX_LENGTH}
            w={300}
          />
        </Group>

        {!isConfigured ? (
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
            title="Log access is not configured"
          >
            Application log access is not configured for this environment, so
            there are no entries to show.
          </Alert>
        ) : (
          <>
            {isPartial && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                color="yellow"
                variant="light"
                title="Partial results"
              >
                Azure returned an incomplete result, so some entries may be
                missing. Narrow the time range or the search to see the rest.
              </Alert>
            )}
            {hasMore && (
              <Alert
                icon={<IconInfoCircle size={16} />}
                color="blue"
                variant="light"
                title="More entries match"
              >
                {`Showing the newest ${APPLOG_LIMIT} entries. Narrow the time range or the filters to see the rest.`}
              </Alert>
            )}
            <ApplogsTable
              entries={entries}
              isLoading={applogsQuery.isLoading}
              isFetching={applogsQuery.isFetching}
              isError={applogsQuery.isError}
              error={
                applogsQuery.error
                  ? getApplogsErrorMessage(applogsQuery.error)
                  : undefined
              }
              onRefresh={() => void applogsQuery.refetch()}
              onSelect={setSelectedEntry}
            />
          </>
        )}
      </Stack>
      <ApplogDetailModal
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />
    </>
  );
};
